// Tree diagram (Year 8, wording approved by the owner, 2 Nov 2026). A
// `tree_diagram` lists the outcomes of two steps, drawn left to right:
// `first` is the branches of the first step, and `second` the branches
// after each of them. `second` is one list when every first branch has the
// same branches after it, or one list per first branch when they differ.
// Each branch ends in a short word, such as "Juice", "H" or "Red".
//
// No numbers or probabilities are ever drawn on the branches (the owner's
// decision, 2 Nov 2026): probabilities come from counting outcomes.
//
// Wrong trees are drawn too, because a question may ask which of several
// trees shows every outcome: a first branch can have no branches after it
// (an empty list), and a tree with no `second` at all is all its outcomes
// side by side in one step.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export type TreeDiagramSpec = {
  type: 'tree_diagram';
  first: string[];
  // One list per first branch, in the same order; null for a tree of one
  // step.
  second: string[][] | null;
};

const MAX_WORD = 12;
const MAX = { first: 4, second: 4, oneStep: 6 };
const MIN = { first: 2, second: 2 };

function readWord(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const word = value.trim();
  if (word === '' || word.length > MAX_WORD) return null;
  // A word on a branch is never an answer box.
  if (/[[\]]/.test(word)) return null;
  return word;
}

// A list of branches from one point: no word twice.
function readBranches(value: unknown, min: number, max: number) {
  if (!Array.isArray(value) || value.length < min || value.length > max)
    return null;
  const words: string[] = [];
  for (const entry of value as unknown[]) {
    const word = readWord(entry);
    if (word === null || words.includes(word)) return null;
    words.push(word);
  }
  return words;
}

export function readTreeDiagram(
  record: Record<string, unknown>,
): TreeDiagramSpec | null {
  if (record.second === undefined) {
    const first = readBranches(record.first, MIN.first, MAX.oneStep);
    return first && { type: 'tree_diagram', first, second: null };
  }
  const first = readBranches(record.first, MIN.first, MAX.first);
  if (!first || !Array.isArray(record.second)) return null;
  const given = record.second as unknown[];
  let second: string[][];
  if (given.every((entry) => typeof entry === 'string')) {
    // The same branches after each first branch.
    const same = readBranches(given, MIN.second, MAX.second);
    if (!same) return null;
    second = first.map(() => [...same]);
  } else {
    if (given.length !== first.length) return null;
    second = [];
    for (const entry of given) {
      // No branches after it, or 2 to 4.
      const list =
        Array.isArray(entry) && entry.length === 0
          ? []
          : readBranches(entry, MIN.second, MAX.second);
      if (!list) return null;
      second.push(list);
    }
    // A tree with nothing after any first branch is a tree of one step.
    if (second.every((list) => list.length === 0)) return null;
  }
  return { type: 'tree_diagram', first, second };
}

// "Apple", "Apple or Muffin", "Apple, Muffin or Yoghurt".
function spokenChoice(words: string[]): string {
  if (words.length < 2) return words.join('');
  return `${words.slice(0, -1).join(', ')} or ${words[words.length - 1] ?? ''}`;
}

export function describeTreeDiagram(tree: TreeDiagramSpec): string {
  const { first, second } = tree;
  if (second === null)
    return `A tree diagram in 1 step. First step: ${spokenChoice(first)}.`;
  const parts = [
    'A tree diagram in 2 steps.',
    `First step: ${spokenChoice(first)}.`,
  ];
  const [one = []] = second;
  const same =
    one.length > 0 &&
    second.every(
      (list) =>
        list.length === one.length &&
        list.every((word, index) => word === one[index]),
    );
  if (same) {
    parts.push(`Second step, after each: ${spokenChoice(one)}.`);
  } else {
    first.forEach((word, index) => {
      const after = second[index] ?? [];
      parts.push(
        after.length === 0
          ? `After ${word}: no branches.`
          : `After ${word}: ${spokenChoice(after)}.`,
      );
    });
  }
  return parts.join(' ');
}

// ---------------------------------------------------------------------------
// Where everything is drawn, in units close to one pixel each at the
// picture's natural width. The tree starts at a point on the left, at
// x = 0, and each branch is a straight line ending just before its word.

export type TreePoint = { x: number; y: number };
export type TreeWord = TreePoint & { text: string; step: 1 | 2 };

export type TreePicture = {
  start: TreePoint;
  branches: { from: TreePoint; to: TreePoint; step: 1 | 2 }[];
  // Each word starts at its x (drawn left-aligned).
  words: TreeWord[];
  box: { x: number; y: number; width: number; height: number };
};

export const TREE_LABEL_SIZE = 14;
const LETTER_WIDTH = 8.4;
const TEXT_HEIGHT = 15;
// Between the words at the ends of the second step.
const ROW = 28;
// Extra room between the groups after each first branch.
const GROUP_GAP = 10;
// Between the end of a branch and its word.
const GAP = 6;
const STEP = { first: { least: 80, most: 140 }, second: 90 };
const PAD = 6;

function widthOf(text: string): number {
  return text.length * LETTER_WIDTH;
}

export function treeDiagramPicture(tree: TreeDiagramSpec): TreePicture {
  const { first, second } = tree;
  // Where each first branch ends, top to bottom, and the rows of words
  // after it.
  const rows: number[][] = [];
  const firstY: number[] = [];
  let y = 0;
  first.forEach((_, index) => {
    const after = second?.[index] ?? [];
    const count = Math.max(1, after.length);
    const group = Array.from({ length: count }, (_, row) => y + row * ROW);
    rows.push(after.length === 0 ? [] : group);
    firstY.push(y + ((count - 1) * ROW) / 2);
    y += count * ROW + (second === null ? 0 : GROUP_GAP);
  });
  const top = firstY[0] ?? 0;
  const bottom = firstY[firstY.length - 1] ?? 0;
  const start = { x: 0, y: (top + bottom) / 2 };
  // The wider the first step fans out, the longer its branches, so they
  // never get too steep to tell apart.
  const reach = Math.min(
    STEP.first.most,
    Math.max(STEP.first.least, (bottom - top) * 0.45),
  );
  const branches: TreePicture['branches'] = [];
  const words: TreeWord[] = [];
  first.forEach((word, index) => {
    const end = { x: reach, y: firstY[index] ?? 0 };
    branches.push({ from: start, to: end, step: 1 });
    words.push({ x: reach + GAP, y: end.y, text: word, step: 1 });
  });
  // The second step starts just after the longest first word, so every
  // branch of the second step is the same length.
  const longest = Math.max(...first.map(widthOf));
  const second0 = reach + GAP + longest + GAP;
  const second1 = second0 + STEP.second;
  first.forEach((_, index) => {
    const from = { x: second0, y: firstY[index] ?? 0 };
    (second?.[index] ?? []).forEach((word, row) => {
      const to = { x: second1, y: rows[index]?.[row] ?? 0 };
      branches.push({ from, to, step: 2 });
      words.push({ x: second1 + GAP, y: to.y, text: word, step: 2 });
    });
  });
  const xs = [0];
  const ys = [start.y];
  for (const word of words) {
    xs.push(word.x + widthOf(word.text));
    ys.push(word.y - TEXT_HEIGHT / 2, word.y + TEXT_HEIGHT / 2);
  }
  const x = Math.min(...xs) - PAD;
  const boxY = Math.min(...ys) - PAD;
  return {
    start,
    branches,
    words,
    box: {
      x,
      y: boxY,
      width: Math.max(...xs) + PAD - x,
      height: Math.max(...ys) + PAD - boxY,
    },
  };
}
